"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ROUTES } from "@/lib/constants";

const CHECKLIST = [
  "Required network type",
  "Required country, city, ISP, ASN, or mobile network",
  "Rotating, sticky, or static session behavior",
  "Whether rotation happens per connection, time interval, or explicit session",
  "HTTP(S), SOCKS5, or other protocol requirements",
  "Username/password and IP-allowlist authentication",
  "Billing by GB, proxy, request, result, or browser time",
  "Minimum purchase and renewal cadence",
  "Bandwidth expiration or rollover",
  "Concurrency and usage limits",
  "Dashboard and API controls",
  "Sourcing, verification, abuse prevention, and security documentation",
  "Results from a representative test against your own targets",
];

export default function BuyerChecklist() {
  const reduced = useReducedMotion() ?? false;

  return (
    <section
      aria-labelledby="buyer-checklist-heading"
      className="border-border py-section relative border-b"
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5 }}
        >
          <p className="text-muted-foreground mb-3 text-xs font-semibold">Buyer checklist</p>
          <h2
            id="buyer-checklist-heading"
            className="text-foreground mb-3 scroll-mt-40 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            How to choose a proxy server
          </h2>
          <p className="text-muted-foreground mb-8 text-base leading-relaxed">
            Before purchasing, check:
          </p>

          <ol className="flex list-none flex-col gap-4">
            {CHECKLIST.map((item, i) => (
              <li key={item} className="flex items-start gap-3.5">
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums"
                  style={{
                    background: "color-mix(in srgb, var(--brand-green) 8%, transparent)",
                    color: "var(--brand-green)",
                    border: "1px solid color-mix(in srgb, var(--brand-green) 20%, transparent)",
                  }}
                >
                  {i + 1}
                </span>
                <span className="text-muted-foreground pt-0.5 text-sm leading-relaxed sm:text-base">
                  {item}
                </span>
              </li>
            ))}
          </ol>

          <p className="text-muted-foreground mt-8 text-sm leading-relaxed">
            Once you know what you need, compare plans on the{" "}
            <Link
              href={ROUTES.pricing}
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              pricing page
            </Link>
            .
          </p>
        </motion.div>
      </div>
    </section>
  );
}
